import { Component, OnInit, OnDestroy } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { environment } from "environments/environment";
import { MessagesService } from "../../services/messages.service";
import { Router } from '@angular/router';
import { UserService } from '../../services/user.service';
import { PendingDownload } from './components/epa-pending-download/epa-pending-download.component';

const BASE_URL = `${environment.baseUrl}/api`;
const POLL_INTERVAL_MS = 3000;

enum EpaJobStatus {
  PENDING = 'PENDING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

interface EpaResponse {
  documents_found: string[];
  documents_not_found: string[];
  download_url: string;
}

interface EpaListResponse {
  claims: string[];
}

interface EpaDownloadResponse {
  status: EpaJobStatus;
}

interface EpaStatusResponse {
  status: EpaJobStatus;
  totalDocuments: number;
  processedDocuments: number;
}

interface CompletedDownload {
  claimNumber: string;
  documentsFound: string[];
  documentsNotFound: string[];
  expanded: boolean;
}

@Component({
  selector: 'app-epa',
  templateUrl: './epa.component.html',
  styleUrls: ['./epa.component.scss']
})
export class EpaComponent implements OnInit, OnDestroy {
  documentId: string = '';
  isDownloading = false;
  isLoadingList = false;
  claimsList: string[] = [];

  pendingDownloads: PendingDownload[] = [];
  completedDownloads: CompletedDownload[] = [];

  private pollTimers = new Map<string, ReturnType<typeof setInterval>>();

  constructor(
    private httpClient: HttpClient,
    private messagesService: MessagesService,
    private userService: UserService,
    private router: Router
  ) {}

  async ngOnInit(): Promise<void> {
    const user = await this.userService.getUser();
    if (!user?.ruoli?.includes('EpaUser')) {
      this.router.navigate(['/unauthorized']);
    }
  }

  ngOnDestroy(): void {
    this.pollTimers.forEach(timer => clearInterval(timer));
  }

  onDocumentIdChange(): void {
    this.claimsList = [];
    this.formatNumDanno();
  }

  private formatNumDanno(): void {
    const id = this.documentId || '';
    const parts = id.split('-');

    let numDannoIdx = -1;
    if (parts.length === 4) {
      numDannoIdx = 3;
    } else if (parts.length === 3 && /^\d{4,}$/.test(parts[1])) {
      numDannoIdx = 2;
    }

    if (numDannoIdx === -1) return;

    const segment = parts[numDannoIdx];
    if (segment.length === 0 || !/^\d+$/.test(segment)) return;

    const stripped = segment.replace(/^0+/, '') || '0';
    const limited = stripped.length > 9 ? stripped.slice(0, 9) : stripped;
    const padded = limited.padStart(9, '0');

    if (padded !== segment) {
      parts[numDannoIdx] = padded;
      this.documentId = parts.join('-');
    }
  }

  private readonly CLAIM_REGEX_FULL    = /^\d{3}-[A-Za-z0-9]{3}-\d{4}-\d{8,9}$/;
  private readonly CLAIM_REGEX_PARTIAL = /^\d{3}-\d{4}-\d{8,9}$/;

  get hasAgencyCode(): boolean {
    return this.CLAIM_REGEX_FULL.test((this.documentId || '').trim());
  }

  get isPartialClaimNumber(): boolean {
    return this.CLAIM_REGEX_PARTIAL.test((this.documentId || '').trim());
  }

  get isActionDisabled(): boolean {
    const id = (this.documentId || '').trim();
    return (
      this.isDownloading ||
      this.isLoadingList ||
      (!this.CLAIM_REGEX_FULL.test(id) && !this.CLAIM_REGEX_PARTIAL.test(id))
    );
  }

  get actionButtonLabel(): string {
    return this.isPartialClaimNumber ? 'Cerca sinistri' : 'Scarica documenti';
  }

  async onAction(): Promise<void> {
    if (this.isPartialClaimNumber) {
      await this.cercaSinistri();
    } else if (this.hasAgencyCode) {
      await this.scaricaDocumenti();
    }
  }

  async cercaSinistri(): Promise<void> {
    this.isLoadingList = true;
    this.claimsList = [];
    const url = `${BASE_URL}/epa/list?claimNumber=${this.documentId.trim()}`;

    try {
      const response = await lastValueFrom(this.httpClient.get<EpaListResponse>(url));
      this.claimsList = response.claims ?? [];
      if (this.claimsList.length === 0) {
        this.messagesService.addMessage({ text: 'Nessun sinistro trovato per il codice inserito', type: 'error' });
      }
    } catch (err: any) {
      console.error('List failed', err);
      const status = err instanceof HttpErrorResponse ? err.status : err?.status;
      if (status === 404) {
        this.messagesService.addMessage({ text: 'Nessun sinistro trovato', type: 'error' });
      } else {
        this.messagesService.addMessage({ text: 'Errore durante il recupero della lista sinistri', type: 'error' });
      }
    } finally {
      this.isLoadingList = false;
    }
  }

  async selezionaSinistro(claim: string): Promise<void> {
    this.documentId = claim;
    this.claimsList = [];
    await this.scaricaDocumenti();
  }

  async scaricaDocumenti(): Promise<void> {
    const claimNumber = this.documentId.trim();

    if (this.pollTimers.has(claimNumber)) {
      this.messagesService.addMessage({ text: 'Download già in corso per questo sinistro', type: 'warning' });
      return;
    }

    this.isDownloading = true;
    const url = `${BASE_URL}/epa/download?claimNumber=${claimNumber}`;

    try {
      const response = await lastValueFrom(this.httpClient.get<EpaResponse | EpaDownloadResponse>(url, { observe: 'response' }));

      if (response.status === 200) {
        const body = response.body as EpaResponse;
        this.addCompletedDownload(claimNumber, body.documents_found ?? [], body.documents_not_found ?? []);
        this.downloadFile(body.download_url, `${claimNumber}.zip`);
      } else if (response.status === 202) {
        this.addPendingDownload(claimNumber);
        this.startPolling(claimNumber);
      }
    } catch (err: any) {
      console.error('Download failed', err);
      const status = err instanceof HttpErrorResponse ? err.status : err?.status;
      if (status === 404) {
        this.messagesService.addMessage({ text: 'Documenti sinistro non trovati', type: 'error' });
      } else if (status === 500) {
        this.messagesService.addMessage({ text: 'Elaborazione precedente fallita. Contattare il supporto.', type: 'error' });
      } else {
        this.messagesService.addMessage({ text: 'Si è verificato un errore durante il download dei documenti', type: 'error' });
      }
    } finally {
      this.isDownloading = false;
    }
  }

  clearCompletedDownloads(): void {
    this.completedDownloads = [];
  }

  toggleCompletedDownload(result: CompletedDownload): void {
    result.expanded = !result.expanded;
  }

  private addCompletedDownload(claimNumber: string, documentsFound: string[], documentsNotFound: string[]): void {
    const existing = this.completedDownloads.find(d => d.claimNumber === claimNumber);
    if (existing) {
      existing.documentsFound = documentsFound;
      existing.documentsNotFound = documentsNotFound;
    } else {
      this.completedDownloads = [{ claimNumber, documentsFound, documentsNotFound, expanded: false }, ...this.completedDownloads];
    }
  }

  private addPendingDownload(claimNumber: string): void {
    if (!this.pendingDownloads.find(p => p.claimNumber === claimNumber)) {
      this.pendingDownloads = [...this.pendingDownloads, { claimNumber, totalDocuments: 0, processedDocuments: 0 }];
    }
  }

  private startPolling(claimNumber: string): void {
    const timer = setInterval(() => this.pollStatus(claimNumber), POLL_INTERVAL_MS);
    this.pollTimers.set(claimNumber, timer);
  }

  private async pollStatus(claimNumber: string): Promise<void> {
    const url = `${BASE_URL}/epa/status?claimNumber=${claimNumber}`;
    try {
      const response = await lastValueFrom(this.httpClient.get<EpaStatusResponse>(url));

      this.updatePendingProgress(claimNumber, response.totalDocuments, response.processedDocuments);

      if (response.status === EpaJobStatus.COMPLETED) {
        this.stopPolling(claimNumber);
        await this.downloadReadyZip(claimNumber);
      } else if (response.status === EpaJobStatus.FAILED) {
        this.stopPolling(claimNumber);
        this.removePendingDownload(claimNumber);
        this.messagesService.addMessage({ text: `Elaborazione del sinistro ${claimNumber} fallita`, type: 'error' });
      }
    } catch (err) {
      console.error(`Polling failed for ${claimNumber}`, err);
    }
  }

  private async downloadReadyZip(claimNumber: string): Promise<void> {
    const url = `${BASE_URL}/epa/download?claimNumber=${claimNumber}`;
    try {
      const response = await lastValueFrom(this.httpClient.get<EpaResponse>(url));
      this.addCompletedDownload(claimNumber, response.documents_found ?? [], response.documents_not_found ?? []);
      this.removePendingDownload(claimNumber);
      this.downloadFile(response.download_url, `${claimNumber}.zip`);
    } catch (err) {
      console.error(`Final download failed for ${claimNumber}`, err);
      this.removePendingDownload(claimNumber);
      this.messagesService.addMessage({ text: `Errore nel download finale del sinistro ${claimNumber}`, type: 'error' });
    }
  }

  private updatePendingProgress(claimNumber: string, totalDocuments: number, processedDocuments: number): void {
    this.pendingDownloads = this.pendingDownloads.map(p =>
      p.claimNumber === claimNumber ? { ...p, totalDocuments, processedDocuments } : p
    );
  }

  private stopPolling(claimNumber: string): void {
    const timer = this.pollTimers.get(claimNumber);
    if (timer) {
      clearInterval(timer);
      this.pollTimers.delete(claimNumber);
    }
  }

  private removePendingDownload(claimNumber: string): void {
    this.pendingDownloads = this.pendingDownloads.filter(p => p.claimNumber !== claimNumber);
  }

  private downloadFile = (downloadUrl: string, filename: string) => {
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };
}
